import { Button, Col, Flex, Form, Input, Row, Select, Skeleton, Tooltip, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { ErrorMessage } from '@hookform/error-message'
import SelectCpn from './SelectCpn'
import { getExceptionRuleViewDetail } from 'src/app/shared/services/ACHExceptionRuleService'
import { creaditOrDebitOptions, ruleTypeOptions } from './constant'
import { useSelector } from 'react-redux'
import { getUserProfile } from 'src/app/store/auth'
import { convertAllSecCode, numberWithCommas } from 'src/app/shared/utils'
import './index.scss'
import { getLookupValues } from 'src/app/store/masterSetup'
import { getAccountList, getClientListName } from 'src/app/shared/services/ClientSetupService'

const ViewACHExceptionRule = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams()
  const [clientList, setClientList] = useState<any[]>([])
  const [accountList, setAccountList] = useState<any[]>([])
  const [isFetched, setIsFetched] = useState(true)
  const [secCode, setSecCode] = useState([])
  const [detail, setDetail] = useState<any>(null)

  const userProfile = useSelector(getUserProfile)
  const lookupValues = useSelector(getLookupValues)

  const defaultValues = {
    clientNames: [],
    accountNames: [],
    allAccounts: false,
    allClients: false,
    ruleDescription: '',
    ruleType: null,
    achOriginatorId: '',
    secCode: [],
    creditOrDebit: null,
    amountFrom: null,
    amountTo: null,
  }

  const {
    control,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<any>({
    mode: 'all',
    defaultValues,
  })

  const curentClients = useWatch({
    control,
    name: 'clientNames',
  })

  const handleCancel = () => {
    navigate(menuUrls.achExceptionRules)
  }

  useEffect(() => {
    if (!curentClients?.length) return
    getAccountList(curentClients[0]).then((res: any) => {
      if (res?.data) {
        setAccountList(
          res?.data?.map((item: any) => ({
            label: item?.accountName,
            value: item?.accountId,
          })),
        )
        // NOTE: use this tricks to remove inactive account from accountList
        setValue(
          'accountNames',
          detail?.accountIds?.filter((item: string) => res?.data.findIndex((i: any) => i.accountId === item) !== -1) ||
            [],
        )
      }
    })
  }, [curentClients, detail?.accountIds, setValue])

  useEffect(() => {
    setIsFetched(false)
    getClientListName().then((res: any) => {
      if (res?.data) {
        setClientList(
          res?.data?.map((item: any) => ({
            label: item.clientName,
            value: item.id,
          })),
        )
      }
    })
    if (id) {
      getExceptionRuleViewDetail(id)
        .then((res: any) => {
          if (res?.data) {
            setDetail(res?.data)
            setValue('clientNames', res?.data?.clientIds || null)
            setValue('allAccounts', res?.data?.allAccount)
            setValue(
              'amountFrom',
              res?.data?.amountFrom !== null
                ? numberWithCommas(Number(res?.data?.amountFrom).toFixed(3).slice(0, -1))
                : null,
            )
            setValue(
              'amountTo',
              res?.data?.amountTo !== null
                ? numberWithCommas(Number(res?.data?.amountTo).toFixed(3).slice(0, -1))
                : null,
            )
            setValue('ruleDescription', res?.data?.description)
            setValue('ruleType', res?.data?.ruleType)
            setValue('achOriginatorId', res?.data?.achOriginatorID)
            setValue('secCode', res?.data?.secCodeIds || [])
            setValue('creditOrDebit', res?.data?.creditDebit)
            if (res?.data?.allAccount) {
              setValue('accountNames', [])
            } else setValue('accountNames', res?.data?.accountIds || [])
          }
          setIsFetched(true)
        })
        .catch((error) => {
          navigate(menuUrls.pageNotFound)
        })
    }

    if (lookupValues && lookupValues?.SECCode?.length > 0) {
      const data = convertAllSecCode(lookupValues?.SECCode)

      setSecCode(
        data?.map((item: any) => ({
          label: item.value,
          value: item.id,
        })) as any,
      )
    }
  }, [id])

  return (
    <Form
      name='view-exception-rule-form'
      className='form-container view-ach-exception-rules read-only'
      layout='vertical'
    >
      {isFetched ? (
        <Flex vertical>
          <Form.Item label={t('ACHExceptionRule:client.selectClient')} className='isRequired '>
            <Controller
              name='clientNames'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  mode='multiple'
                  style={{ width: '100%' }}
                  maxTagCount='responsive'
                  options={clientList}
                  disabled
                  maxTagPlaceholder={(omittedValues) => (
                    <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                      <span>+{omittedValues?.length}</span>
                    </Tooltip>
                  )}
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='clientNames' />
            </Typography>
          </Form.Item>

          <SelectCpn
            control={control}
            errors={errors}
            title={t('ACHExceptionRule:account.selectAccount')}
            checkboxTitle={t('ACHExceptionRule:account.allAccounts')}
            tooltipTitle={t('ACHExceptionRule:account.tooltip')}
            placeholder={''}
            options={accountList}
            selectName='accountNames'
            defaultSelectedValue={[]}
            checkboxName='allAccounts'
            defaultCheckboxValue={false}
            clearErrors={clearErrors}
          />

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:ruleDescription.title')} className='isRequired'>
                <Controller
                  name='ruleDescription'
                  control={control}
                  render={({ field }) => <Input {...field} className='ant-input-view-only' disabled maxLength={255} />}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='ruleDescription' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:ruleType.title')} className='isRequired'>
                <Controller
                  name='ruleType'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      style={{ width: '100%' }}
                      maxTagCount='responsive'
                      options={ruleTypeOptions}
                      disabled
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='ruleType' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:achOriginatorId.title')}>
                <Controller
                  name='achOriginatorId'
                  control={control}
                  render={({ field }) => <Input {...field} className='ant-input-view-only' disabled maxLength={255} />}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='achOriginatorId' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:secCode.title')}>
                <Controller
                  name='secCode'
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      style={{ width: '100%' }}
                      maxTagCount='responsive'
                      mode='multiple'
                      options={secCode}
                      maxTagPlaceholder={(omittedValues) => (
                        <Tooltip title={omittedValues.map(({ label }) => label).join(', ')}>
                          <span>+{omittedValues?.length}</span>
                        </Tooltip>
                      )}
                      disabled
                    />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='secCode' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t('ACHExceptionRule:creditOrDebit.title')} className='isRequired'>
            <Controller
              name='creditOrDebit'
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  style={{ width: '100%' }}
                  maxTagCount='responsive'
                  options={creaditOrDebitOptions}
                  disabled
                />
              )}
            />
            <Typography className='error-message'>
              <ErrorMessage errors={errors} name='creditOrDebit' />
            </Typography>
          </Form.Item>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:amountFrom.title')}>
                <Controller
                  name='amountFrom'
                  control={control}
                  render={({ field }) => (
                    <Input {...field} className='ant-input-view-only' disabled maxLength={13} suffix={'$'} />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='amountFrom' />
                </Typography>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item label={t('ACHExceptionRule:amountTo.title')}>
                <Controller
                  name='amountTo'
                  control={control}
                  render={({ field }) => (
                    <Input {...field} className='ant-input-view-only' disabled maxLength={13} suffix={'$'} />
                  )}
                />
                <Typography className='error-message'>
                  <ErrorMessage errors={errors} name='amountTo' />
                </Typography>
              </Form.Item>
            </Col>
          </Row>
        </Flex>
      ) : (
        <Skeleton active />
      )}
      <Flex className='button-group' justify='flex-end' gap={24}>
        <Button className='cancel-button' onClick={handleCancel}>
          {t('common:back_btn')}
        </Button>
      </Flex>
    </Form>
  )
}

export default ViewACHExceptionRule
